import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  GetContextMenuItemsParams,
  GridApi,
  GridReadyEvent,
  IServerSideGetRowsParams,
  MenuItemDef,
} from 'ag-grid-community'
import { useDispatch, useSelector } from 'react-redux'
import { setMenu } from '../../../infrastructure/store/sidebar/sidebarSlice'

import { reportsColumns } from '../../../infrastructure/constant/columns/reportsColumns'
import AgList from '../../../components/commons/AgGrid/AgList'
import { settingsService } from '../../../infrastructure/services/settingsService'

import {
  actionContextMenu,
  formattedFilters,
} from '../../../infrastructure/utils/common'
import DeleteModal from '../../../components/commons/modals/DeleteModal/DeleteModal'
import storageService from '../../../infrastructure/services/storageService'
import { checkPermissions } from '../../../infrastructure/utils/permissions'
import { UserRoles } from '../../../infrastructure/interfaces/users'

const ReportList = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const menu = useSelector((state: any) => state.sidebar.menu)
  const user =
    useSelector((state: any) => state.auth) || storageService.getUser()
  const [gridApi, setGridApi] = useState<GridApi>()
  const [deleteModal, setDeleteModal] = useState<any>(false)
  const [loadedData, setLoadedData] = useState<boolean>(false)
  const [perPage, setPerPage] = useState<number>(14)
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [totalPages, setTotalPages] = useState<number>(1)
  const navigate = useNavigate()

  const getContextMenu = (row: GetContextMenuItemsParams) => {
    if (!row.value) return []
    const items: MenuItemDef[] = []

    let currentUser = user?.user || storageService.getUser()

    if (
      checkPermissions(
        ['reports.*', 'reports.edit'],
        currentUser?.permissions
      ) ||
      currentUser?.role?.name === UserRoles.ADMIN
    ) {
      items.push({
        name: 'Editar',
        action: () =>
          actionContextMenu(
            'EDIT',
            row,
            null,
            location.pathname + '/create-report',
            navigate
          ),
        cssClasses: ['tw-cursor-pointer'],
      })
    }
    if (
      checkPermissions(
        ['reports.*', 'reports.delete'],
        currentUser?.permissions
      ) ||
      currentUser?.role?.name === UserRoles.ADMIN
    ) {
      items.push({
        name: 'Eliminar',
        action: () => actionContextMenu('DELETE', row, setDeleteModal),
        cssClasses: ['tw-text-alert', 'tw-cursor-pointer'],
      })
    }
    return items
  }

  const handleDelete = () => {
    settingsService
      .deleteReport(deleteModal.id)
      .then((res) => {
        if (menu && menu.length) {
          dispatch(
            setMenu(menu.filter((item: any) => item.slug !== deleteModal.slug))
          )
        }
        setDeleteModal(false)
        gridApi?.refreshServerSide({ purge: true })
      })
      .catch((err) => {
        setDeleteModal(false)
      })
  }

  const onGridReady = (params: GridReadyEvent) => {
    setLoadedData(true)
    setGridApi(params.api)
    const updateData = (data: any) => {
      let idSequence = 1
      data?.items?.forEach(function (item: any) {
        item.id = idSequence++
      })

      const datasource = {
        getRows(params: IServerSideGetRowsParams) {
          let filters: any = null
          if (
            params.request.filterModel &&
            Object.keys(params.request.filterModel).length > 0
          ) {
            filters = formattedFilters(params.request.filterModel)
          }

          if (params.request.sortModel.length > 0) {
            filters = {
              ...filters,
              order_field: params.request.sortModel[0].colId,
              order_direction: params.request.sortModel[0].sort,
            }
          }

          settingsService
            .getReports({
              page: params.api.paginationGetCurrentPage() + 1,
              per_page: perPage,
              from: params.request.endRow,
              ...filters,
            })
            .then((response) => {
              params.success({
                rowData: response.data.items,
                rowCount: response.data.meta.total,
              })
              setTotalPages(response.data.meta.last_page)
              setCurrentPage(response.data.meta.current_page)
              setLoadedData(false)
            })
            .catch((error) => {
              params.fail()
              setLoadedData(false)
            })
        },
      }
      params.api.setServerSideDatasource(datasource)
    }

    updateData(params.api)
  }

  return (
    <>
      <AgList
        columns={reportsColumns}
        loading={loadedData}
        gridApi={gridApi}
        currentPage={currentPage}
        totalPages={totalPages}
        onGridReady={onGridReady}
        getContextMenu={getContextMenu}
        pagination={true}
        paginationPageSize={perPage}
        cacheBlockSize={perPage}
        onRowDoubleClicked={(row) =>
          actionContextMenu(
            'EDIT',
            row,
            null,
            location.pathname + '/create-report',
            navigate
          )
        }
      />
      <DeleteModal
        isOpen={deleteModal}
        onClose={() => setDeleteModal(false)}
        description={'¿Está seguro que desea eliminar este registro?'}
        onConfirm={handleDelete}
        title={t('titles.Eliminar informe')}
      />
    </>
  )
}

export default ReportList
